import { LitComponent } from "./lit-component";
import { FloatingUiController } from "../controllers/FloatingUiController";

import type { PropertyDeclarations, PropertyValueMap } from "lit";
import type { FloatSettings } from "../controllers/FloatingUiController";

export class Button extends LitComponent {
  static properties: PropertyDeclarations = {
    href: { type: String },
    text: { type: String },
    hint: { type: String },
    selected: { type: Boolean },
    disabled: { type: Boolean },
    showIcon: { type: Boolean, attribute: "show-icon" },
    showText: { type: Boolean, attribute: "show-text" },
    icon: { type: String },
    iconUnstyled: { type: Boolean, attribute: "icon-unstyled" },
    fullWidth: { type: Boolean, attribute: "full-width" },
    alignSelf: { type: String, attribute: "align-self" },
    alignContent: { type: String, attribute: "align-content" },
    floating: { type: Object },
    counter: { type: Object },
    counterFloating: { state: true },
  };

  href?: string;
  text = "";
  hint = "";
  selected = false;
  disabled = false;
  showIcon = false;
  showText = false;
  icon?: string;
  iconUnstyled = false;
  fullWidth = false;
  alignSelf?: AlignmentSetting = "center";
  alignContent?: AlignmentSetting = "center";
  floating?: FloatSettings;

  counter?: {
    value?: number;
    showNumber?: boolean;
    showBrackets?: boolean;
    floating?: FloatSettings;
    hideWhenZero?: boolean;
  };

  counterFloating?: FloatSettings;

  get iconElement() {
    return this.querySelector(".wk-icon");
  }

  private floatingUi: FloatingUiController = new FloatingUiController(this);

  render() {
    const alignSelf = this.alignSelf ?? "center";
    const alignContent = this.alignContent ?? "center";

    const classes = [
      "wk-button",
      `wk-align-${alignSelf}`,
      `wk-align-content-${alignContent}`,
      this.selected && "wk-selected",
      this.fullWidth && "wk-full-width",
    ]
      .filter((v) => !!v)
      .join(" ");

    if (this.href) {
      return this.lit.html`
        <a
          href=${this.href}
          aria-label=${this.text ?? this.hint}
          class=${classes}
          rel="noopener noreferrer nofollow"
        >
          ${this.renderIcon()}${this.renderText()}${this.renderCounter()}
        </a>
      `;
    }

    return this.lit.html`
      <button
        type="button"
        aria-label=${this.text ?? this.hint}
        class=${classes}
        ?disabled=${this.disabled}
      >
        ${this.renderIcon()}${this.renderText()}${this.renderCounter()}
      </button>
    `;
  }

  renderIcon() {
    if (!this.showIcon || !this.icon) {
      return;
    }

    return this.lit.html`
      <wk-icon
        class="wk-icon"
        icon=${this.icon}
        ?unstyled=${this.iconUnstyled}
      ></wk-icon>
    `;
  }

  renderText() {
    if (!this.showText) {
      return;
    }

    return this.lit.html`<span class="wk-text">${this.text}</span>`;
  }

  renderCounter() {
    if (!this.counter) {
      return;
    }

    if (this.counter.hideWhenZero && !this.counter.value) {
      return;
    }

    return this.lit.html`
      <wk-counter
        .value=${this.counter?.value}
        .floating=${this.counterFloating}
        .showNumber=${this.counter.showNumber}
        .showBrackets=${this.counter.showBrackets}
      ></wk-counter>
    `;
  }

  protected firstUpdated(
    changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(changedProperties);

    this.counterFloating = this.getCounterFloating();
  }

  getCounterFloating = (): FloatSettings | undefined => {
    if (!this.counter?.floating) return;

    const reference =
      this.counter?.floating?.reference === "icon"
        ? this.iconElement ?? this
        : this;

    return {
      ...this.counter.floating,
      reference,
    };
  };

  createRenderRoot() {
    return this;
  }
}

if (!customElements.get("wk-button")) {
  customElements.define("wk-button", Button);
}

export type AlignmentSetting = "left" | "center" | "right";

declare global {
  interface HTMLElementTagNameMap {
    "wk-button": Button;
  }
}
